import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/styles'
import { Loader2 } from 'lucide-react'
import { useRef, useState } from 'react'

const buttonVariants = cva('hover inline-flex items-center justify-center whitespace-nowrap rounded-8 text-16 font-medium transition-colors disabled:pointer-events-none disabled:opacity-50', {
  variants: {
    variant: {
      default: 'flex items-center bg-[#025be8] text-white hover:opacity-90',
      outline: 'flex items-center border-1 border-[#025be8] bg-white text-[#025be8] hover:opacity-90',
    },
    size: {
      default: 'h-40 px-16 py-8 rounded-38',
      sm: 'h-36 rounded-8 px-12',
      lg: 'h-44 rounded-8 px-32',
      icon: 'h-40 w-40',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'default',
  },
})

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
  // loading
  spin?: boolean
  // 防連點，毫秒
  debounce?: number
  // 是否返回錯誤事件
  reject?: boolean
}

const AlertButton = React.forwardRef<HTMLButtonElement, ButtonProps>(({ reject = false, spin, className, variant, size, asChild = false, ...props }, ref) => {
  const [isLoading, setIsLoading] = useState(false)
  const debounceRef = useRef(0)
  const debounce = props?.debounce ?? 333

  /** 事件等待 */
  const onButtonClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    if (props?.disabled || spin || isLoading) return
    if (debounceRef.current > Date.now()) return
    debounceRef.current = Date.now() + debounce
    try {
      setIsLoading(true)
      await Promise.all([props.onClick?.(event)])
    } catch (e) {
      if (reject) return Promise.reject(e)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <button className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} onClick={onButtonClick}>
      {!!(isLoading || spin) && <Loader2 className="mr-6 h-16 w-16 animate-spin" />}
      {props.children}
    </button>
  )
})
AlertButton.displayName = 'AlertButton'

export { AlertButton, buttonVariants }
